// @flow
import * as React from 'react';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import resourceFactory from 'app/common/lib/ResourceFactory';
import {
  ContainsOnlyAscii,
  EqualTo,
  NewPassword,
  Required,
  type Validator,
} from 'app/common/lib/react/validator';
import { Box, Button, Divider, Label, Text, TextField } from 'gestalt';

type ValidationResult = {|
  errors: Array<string>,
  score?: number,
|};
type RowProps = {|
  children?: React.Element<any>,
  divide?: boolean,
  htmlFor: string,
  label: string,
|};
type PasswordModalProps = {|
  hasPassword: boolean,
  onHide: () => void,
  onPasswordForgotModal: () => void,
  onSuccess: () => void,
  username: string,
|};
type PasswordModalState = {|
  buttonLoading: boolean,
  modalLoading: boolean,
  newPassword?: string,
  newPasswordErrors: Array<string>,
  oldPassword?: string,
  oldPasswordErrors: Array<string>,
  repeatPassword?: string,
  repeatPasswordErrors: Array<string>,
|};

// eslint-disable-next-line no-unused-vars
const stageTranslations = () => {
  // Staging translations: Replacing validators with the versions in app/packages/validators/validator
  // These validators must be passed error messages explicitly
  // See CUI-3594
  i18n._('Required', 'Validation error when a required field is missing');
};

const runValidators = function(validators: Array<Validator>, value: string): ValidationResult {
  let errors = [];
  let scoreObj = {};

  validators.forEach((validator) => {
    const validatorResults = validator(value);
    if (validatorResults && validatorResults.score) {
      scoreObj = { score: validatorResults.score };
    }

    errors = errors.concat(validatorResults.errors);
  });

  return {
    errors,
    ...scoreObj,
  };
};

const Row = ({ children, htmlFor, label, divide }: RowProps) => (
  <Box>
    <Box display="flex" padding={4}>
      <Box alignItems="center" marginRight={3} xs={{ column: 4 }}>
        <Label htmlFor={htmlFor}>
          <Text weight="bold">{label}</Text>
        </Label>
      </Box>
      {children}
    </Box>
    {divide ? <Divider /> : null}
  </Box>
);

export class PasswordModal extends React.Component<PasswordModalProps, PasswordModalState> {
  state = {
    buttonLoading: false,
    modalLoading: false,
    oldPassword: undefined,
    newPassword: undefined,
    repeatPassword: undefined,
    oldPasswordErrors: [],
    newPasswordErrors: [],
    repeatPasswordErrors: [],
  };

  handleSubmit = ({ event }: { event: SyntheticMouseEvent<> }) => {
    event.preventDefault();
    const {
      oldPassword,
      oldPasswordErrors,
      newPassword,
      newPasswordErrors,
      repeatPassword,
      repeatPasswordErrors,
    } = this.state;

    const hasPassword = this.props.hasPassword;

    this.handleButtonLoading(true);
    this.handleBlankFields();

    // Doesn't submit the form when there are client side errors

    // Should we show a message here that submission was blocked?
    if (
      oldPasswordErrors.length ||
      newPasswordErrors.length ||
      repeatPasswordErrors.length ||
      (hasPassword ? oldPassword === undefined : false) ||
      newPassword === undefined ||
      repeatPassword === undefined
    ) {
      this.handleButtonLoading(false);
      return;
    }

    const resourceOptions = {
      new_password: newPassword,
      new_password_confirm: repeatPassword,
      old_password: oldPassword,
    };

    const resource = resourceFactory.create('UserPasswordResource', resourceOptions);

    resource.callUpdate({ showError: false }).then(
      () => {
        this.props.onHide();
        if (this.props.onSuccess) {
          this.props.onSuccess();
        }
      },
      // Should only occur when user enters an incorrect old password
      (denzelError) => {
        const fieldTitle = ((denzelError.extra_data || {}).objects || {}).field || 'old';
        this.setState({ [`${fieldTitle}PasswordErrors`]: [denzelError.message] });
        this.handleButtonLoading(false);
      },
    );
  };

  handleButtonLoading(buttonLoading: boolean) {
    this.setState({ buttonLoading });
  }

  handleModalLoading(modalLoading: boolean) {
    this.setState({ modalLoading });
  }

  handleForgotPassword = () => {
    ga.trackEvent('forgot_password', PCONST.GA_ACTION_CLICK);
    this.handleModalLoading(true);
    const resource = resourceFactory.create('UserResetPasswordResource', {
      username_or_email: this.props.username,
    });
    resource.callCreate({ showError: false }).then(
      () => {
        ga.trackEvent('forgot_password', PCONST.GA_ACTION_SUCCESS);
        this.props.onPasswordForgotModal();
        this.handleModalLoading(false);
      },
      () => {
        ga.trackEvent('forgot_password', PCONST.GA_ACTION_ERROR);
      },
    );
  };

  // Sets error messages for blank fields upon submit for the case where
  // the form is submitted in an untouched state
  handleBlankFields = () => {
    const errors = {};
    if (this.state.oldPassword === undefined) {
      errors.oldPasswordErrors = [i18n._('Required')];
    }
    if (this.state.newPassword === undefined) {
      errors.newPasswordErrors = [i18n._('Required')];
    }
    if (this.state.repeatPassword === undefined) {
      errors.repeatPasswordErrors = [i18n._('Required')];
    }
    this.setState(errors);
  };

  updateAndValidate = (password: string, value: string) => {
    const passwords = {
      oldPassword: this.state.oldPassword,
      newPassword: this.state.newPassword,
      repeatPassword: this.state.repeatPassword,
    };

    passwords[password] = value;

    let oldPasswordErrors = [];
    let newPasswordErrors = [];
    let repeatPasswordErrors = [];

    if (passwords.oldPassword !== undefined) {
      oldPasswordErrors = Required()(passwords.oldPassword).errors;
    }

    if (passwords.newPassword !== undefined) {
      newPasswordErrors = this.validateNewPassword(passwords.newPassword).errors;
    }

    if (passwords.repeatPassword !== undefined && passwords.newPassword !== undefined) {
      repeatPasswordErrors = this.validateRepeatPassword(
        passwords.newPassword,
        passwords.repeatPassword,
      ).errors;
    }

    this.setState({
      [password]: value,
      oldPasswordErrors,
      newPasswordErrors,
      repeatPasswordErrors,
    });
  };

  validateNewPassword(newPassword: string) {
    const validators = [
      Required(),
      ContainsOnlyAscii({ message: i18n.commonStrings.asciiPasswordError }),
      NewPassword({
        username: this.props.username,
        samePasswordStr: i18n._('Oops! Your password shouldn’t match your username.'),
      }),
    ];

    return runValidators(validators, newPassword);
  }

  validateRepeatPassword(newPassword: string, repeatPassword: string) {
    const validators = [
      Required(),
      EqualTo({
        targetValue: newPassword,
        message: i18n._('Passwords don’t match'),
      }),
    ];

    return runValidators(validators, repeatPassword);
  }

  render() {
    const { oldPasswordErrors, newPasswordErrors, repeatPasswordErrors } = this.state;

    const hasPassword = this.props.hasPassword;
    const titleText = hasPassword ? i18n._('Change your password') : i18n._('Set your password');
    const primaryButtonText = hasPassword ? i18n._('Change Password') : i18n._('Save');

    const submittable =
      (hasPassword ? this.state.oldPassword : true) &&
      this.state.newPassword &&
      this.state.repeatPassword &&
      oldPasswordErrors.length === 0 &&
      newPasswordErrors.length === 0 &&
      repeatPasswordErrors.length === 0;

    const footerButtons = (
      <Box alignItems="center" justifyContent="end" marginLeft={-1} marginRight={-1} display="flex">
        <Box paddingX={1}>
          <Button inline onClick={this.props.onHide} text={i18n._('Cancel')} size="md" />
        </Box>
        <Box paddingX={1}>
          <Button
            color="red"
            disabled={!submittable || this.state.buttonLoading}
            inline
            type="submit"
            onClick={this.handleSubmit}
            text={primaryButtonText}
            size="md"
          />
        </Box>
      </Box>
    );

    return (
      <Modal
        accessibilityCloseLabel={i18n._('Close')}
        accessibilityModalLabel={titleText}
        onDismiss={this.props.onHide}
        heading={titleText}
        footer={footerButtons}
        size="md"
      >
        {hasPassword ? (
          <Box>
            <Box display="flex" padding={4}>
              <Box marginRight={3} xs={{ column: 4 }}>
                <Label htmlFor="changePasswordOld">
                  <Text weight="bold">{i18n._('Old password')}</Text>
                </Label>
              </Box>
              <Box flex="grow">
                <TextField
                  autoComplete="current-password"
                  errorMessage={oldPasswordErrors[0]}
                  id="changePasswordOld"
                  name="old"
                  onChange={({ value }) => this.updateAndValidate('oldPassword', value)}
                  type="password"
                  value={this.state.oldPassword}
                />
                <Box marginTop={1} overflow="hidden">
                  <Button
                    inline
                    onClick={this.handleForgotPassword}
                    text={i18n._('Forgot it?')}
                    size="md"
                  />
                </Box>
              </Box>
            </Box>
            <Divider />
          </Box>
        ) : null}
        <Row divide htmlFor="changePasswordNew" label={i18n._('New password')}>
          <Box flex="grow">
            <TextField
              autoComplete="new-password"
              errorMessage={newPasswordErrors[0]}
              id="changePasswordNew"
              name="new"
              onChange={({ value }) => this.updateAndValidate('newPassword', value)}
              type="password"
              value={this.state.newPassword}
            />
          </Box>
        </Row>
        <Row htmlFor="changePasswordNewConfirm" label={i18n._('Type it again')}>
          <Box flex="grow">
            <TextField
              autoComplete="new-password"
              errorMessage={repeatPasswordErrors[0]}
              id="changePasswordNewConfirm"
              name="new_confirm"
              onChange={({ value }) => this.updateAndValidate('repeatPassword', value)}
              type="password"
              value={this.state.repeatPassword}
            />
          </Box>
        </Row>
      </Modal>
    );
  }
}

export default PasswordModal;
